import { useEffect, type ReactNode } from 'react';
import { t, type Leaf, type Locale } from '../../i18n/strings';
import { cx } from '../../lib/cx';
import styles from './xp.module.css';

export interface XpMenuItem {
  readonly id: string;
  readonly label: Leaf;
  readonly disabled?: boolean;
  /** Coche à gauche, pour les entrées qui basculent un réglage. */
  readonly checked?: boolean;
  readonly onSelect: () => void;
}

export interface XpMenu {
  readonly id: string;
  readonly label: Leaf;
  readonly items: readonly XpMenuItem[];
}

interface Props {
  readonly menus: readonly XpMenu[];
  readonly open: string | null;
  readonly lang: Locale;
  readonly onOpen: (id: string | null) => void;
}

/**
 * La barre de menus de l'Explorateur : Fichier, Edition, Affichage, Favoris,
 * Steve, ?.
 *
 * Aucune entrée n'est décorative — **toutes font quelque chose**, même celles
 * qui ne peuvent qu'échouer : « Désinstaller Steve » ouvre une boîte de dialogue
 * qui explique que le programme d'installation reste introuvable. C'est la
 * blague du skin, et c'est aussi ce qui évite une chrome en carton-pâte.
 *
 * Un clic ouvre, un second referme ; survoler une autre entrée pendant qu'un
 * menu est ouvert bascule dessus, comme dans le vrai système. `Échap` referme.
 */
export function XpMenuBar({ menus, open, lang, onOpen }: Props): ReactNode {
  useEffect(() => {
    if (open === null) return;
    const onKey = (event: KeyboardEvent): void => {
      if (event.key === 'Escape') onOpen(null);
    };
    window.addEventListener('keydown', onKey);
    return () => {
      window.removeEventListener('keydown', onKey);
    };
  }, [open, onOpen]);

  return (
    <div className={styles.menuBar} role="menubar">
      {menus.map((menu) => (
        <div key={menu.id} className={styles.menuHolder}>
          <button
            type="button"
            role="menuitem"
            className={cx(styles.menuTitle, open === menu.id && styles.menuTitleOpen)}
            aria-haspopup="true"
            aria-expanded={open === menu.id}
            onClick={() => {
              onOpen(open === menu.id ? null : menu.id);
            }}
            onPointerEnter={() => {
              if (open !== null && open !== menu.id) onOpen(menu.id);
            }}
          >
            {t(menu.label, lang)}
          </button>

          {open === menu.id && (
            <div className={styles.menuDrop} role="menu" aria-label={t(menu.label, lang)}>
              {menu.items.map((item) => (
                <button
                  key={item.id}
                  type="button"
                  role="menuitem"
                  className={styles.menuItem}
                  disabled={item.disabled ?? false}
                  onClick={() => {
                    onOpen(null);
                    item.onSelect();
                  }}
                >
                  <span className={styles.menuCheck} aria-hidden="true">
                    {item.checked === true ? '✓' : ''}
                  </span>
                  <span className={styles.menuLabel}>{t(item.label, lang)}</span>
                </button>
              ))}
            </div>
          )}
        </div>
      ))}
    </div>
  );
}
